import { BybButton } from "@Components/common/byb-ds";
import ResponsiveAlert from "@Components/common/responsive-alert";
import BaseFormField from "@Components/web-portal/base-form-field";
import Link from "@Components/web-portal/link";
import { Actions, AppContext } from "@Context/common/general/context";
import { getAuthErrorMessage } from "@Helpers/utilities-ryan";
import useI18n from "@i18n";
import { Alert, Box, Button, CircularProgress, FormControl, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { isArray } from "lodash-es";
import { FormEvent, useContext, useState } from "react";

interface ISignInForm {
  hideSignUp?: boolean;
  showResetConfirmationMessage?: boolean;
  togglePasswordReset: () => void;
}

const SignInForm = ({ hideSignUp = false, showResetConfirmationMessage = false, togglePasswordReset }: ISignInForm) => {
  const { i18n } = useI18n();
  const { state, dispatch } = useContext(AppContext);
  const theme = useTheme();
  // const socialAuth = useSocialAuth();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = state.device.isMobile || isMdDown;
  const isVR = state.device.isUnityEmbedded;
  // const signInErrorMessage = state.auth.errorMessage;

  const [email, setEmail] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>();
  const [errorOpen, setErrorOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const isValidEmail = (email: string) => {
    setErrorOpen(false);

    const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    if (!regex.test(email)) {
      setErrorMessage(i18n("signin.email.invalid"));
      setErrorOpen(true);
      return false;
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const data = new FormData(event.currentTarget);

    setLoading(true);
    setErrorOpen(false);
    dispatch({
      type: Actions.FirebaseActions.Login,
      disableLoaders: true,
      payload: {
        email: data.get("Email") as string,
        pass: data.get("Password") as string,
      },
      onError: error => {
        //TODO: Messy assumption transforming this array into expected error format
        if (isArray(error) && error.length > 0) error = error[0];
        setErrorMessage(i18n(getAuthErrorMessage(error.code)));
        setErrorOpen(true);
        setLoading(false);
      },
    });
  };

  const ForgotPasswordLink = (props: { styleProps?: any; textVariant: any }) => {
    return (
      <Box
        onClick={togglePasswordReset}
        style={{
          ...props.styleProps,
          cursor: "pointer",
        }}
        role="link"
        data-href="/forgot-password"
      >
        <Typography
          variant={props.textVariant}
          sx={{
            color: "dsPrimary.v400",
            textDecoration: "underline",
            textDecorationColor: "rgba(67, 157, 228, 0.4)",
            ":hover": {
              textDecorationColor: "rgba(67, 157, 228)",
            },
          }}
        >
          {i18n("signin.forgot.password.ingress")}
        </Typography>
      </Box>
    );
  };

  // TODO: uncomment when social login is ready
  // useEffect(() => {
  //   if (signInErrorMessage.length > 0) {
  //     setErrorMessage(signInErrorMessage);
  //   } else {
  //     setErrorMessage(undefined);
  //   }
  // }, [signInErrorMessage]);

  // useEffect(() => {
  //   /**
  //    * We open the error on successful social login because you can successfully log into a google/apple/facebook
  //    * account that is not associated with Be Your Best. This causes a portal login failure. To prevent an error
  //    * message showing up during a complete login (social + BYB), we check the auth error message which is set
  //    * during onAuthStateChanged in auth.jsx
  //    */
  //   if (signInErrorMessage.length > 0) {
  //     setErrorOpen(socialAuth.succeeded);
  //   } else {
  //     if (socialAuth.succeeded) Events.app.navigateExclusive.emit({ path: "/settings" });
  //   }
  // }, [socialAuth.succeeded]);

  return (
    <form onSubmit={handleSubmit} style={{ height: "100%" }}>
      <Typography variant="text4xl700" color="dsBase.v200">
        {i18n("signin.sign.in")}
      </Typography>
      {/* <SocialAuthSection socialAuth={socialAuth} onLoginClick={() => setErrorOpen(false)} /> */}
      <FormControl
        sx={{
          width: "100%",
          display: "flex",
          flexGrow: 1,
          height: isMobile ? "100%" : "inherit",
          marginTop: theme.spacing(4),
        }}
      >
        <ResponsiveAlert errorOpen={errorOpen} errorMessage={errorMessage} setErrorOpen={setErrorOpen} />
        <Stack spacing={3}>
          <Stack>
            <BaseFormField
              id="email"
              name="Email"
              type="email"
              label={i18n("signin.email.label")}
              placeholder={i18n("signin.email.placeholder")}
              onChange={e => setEmail(e.target.value)}
              required
            />

            <Box sx={{ marginTop: "32px" }}>
              <Box sx={{ position: "relative" }}>
                <BaseFormField
                  id="password"
                  name="Password"
                  type="password"
                  label={i18n("signin.password.label")}
                  placeholder={i18n("signin.password.placeholder")}
                  required
                />

                {!isMobile && !isVR && (
                  <ForgotPasswordLink
                    styleProps={{
                      position: "absolute",
                      right: "0px",
                      top: "0px",
                    }}
                    textVariant={"textS500"}
                  />
                )}
              </Box>
            </Box>

            {showResetConfirmationMessage && (
              <Alert severity="success" sx={{ marginTop: "30px" }}>
                {i18n("signin.password.reset.sent")}
              </Alert>
            )}
          </Stack>
        </Stack>

        <Button id="signin-button" sx={{ marginTop: "40px" }} type="submit" onClick={e => isValidEmail(email)} size="large" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : i18n("signin.sign.in.button")}
        </Button>

        {isMobile && !isVR && (
          <Box
            sx={{
              marginTop: "32px",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <ForgotPasswordLink textVariant="textBase500" />
          </Box>
        )}

        {isMobile && !hideSignUp && (
          <Stack
            sx={{
              flexGrow: 1,
            }}
          >
            <Box sx={{ flexGrow: 1 }} />
            <Typography
              sx={{
                marginTop: theme.spacing(3),
                marginBottom: theme.spacing(3),
                textAlign: "center",
              }}
            >
              {i18n("signin.not.a.member")}{" "}
              <Typography variant="textBase500" component="span">
                <Link to="/signup">{i18n("signin.sign.up")}</Link>
              </Typography>
            </Typography>
          </Stack>
        )}
      </FormControl>
    </form>
  );
};

export default SignInForm;
